import { goalClass, type ColKey } from '../campaign/columns';
import { economicsOf } from '../campaign/economics';
import { rank, type State } from '../campaign/status';

/**
 * Отбор и порядок в списке кампаний.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ. Тридцать шесть строк — это уже не список, а таблица,
 * в которой ищут глазами. Вопросы у человека всегда одни и те же:
 * «кто не тратит», «где ДРР хуже всех», «у кого нет лимита». Каждый
 * из них решался прокруткой и памятью.
 *
 * ГЛАВНОЕ — ОТБОР РАБОТАЕТ ВМЕСТЕ С ВЫДЕЛЕНИЕМ. Отобрать «без лимита»,
 * нажать «выделить все», применить действие — вот ради чего это сделано.
 * Отбор, после которого «выделить все» выделяет ВСЕ строки кабинета,
 * а не показанные, — ловушка, и притом дорогая: человек видит три
 * строки, а действие уходит на тридцать шесть.
 *
 * ПОРЯДОК ПО ДРР — САМОЕ ОПАСНОЕ МЕСТО ЗДЕСЬ. У кампании без выручки
 * ДРР не ноль и не бесконечность, а «неизвестно»: считать его нулём
 * значит поставить худшие кампании в начало списка лучших. Такие строки
 * идут в конец при любом направлении сортировки — они не участвуют
 * в сравнении вовсе.
 *
 * ЗДЕСЬ ТОЛЬКО РАЗБОР. Ни базы, ни сети.
 */

export interface Money {
  spend: number;
  revenue: number;
  orders: number;
}

export interface Item {
  id: string;
  name: string | null;
  advertId: number;
  managed: boolean;
  spend: number;
  revenue: number;
  orders: number;
  views: number;
  clicks: number;
  atbs: number;
  /**
   * Остаток на счёте и пять производных — всё приходит с сервера.
   *
   * `null` означает «делить было не на что» либо «ни разу не измеряли»,
   * и ни то, ни другое не равно нулю. При сортировке такие строки уходят
   * вниз: кампания без выручки не «лучшая по ДРР», она неизвестная.
   */
  balance: number | null;
  ctr: number | null;
  crCart: number | null;
  crOrder: number | null;
  drr: number | null;
  cpo: number | null;
  cpc: number | null;
  /** Отмены за период — считаются по дню отмены, из заказов не вычтены. */
  canceled: number;
  /** Расход за вчерашние сутки. От выбранного периода не зависит. */
  spendYest: number;
  /** Вилка ставок CPM кампании, ₽. `null` — ставок не задано вовсе. */
  cpmMin: number | null;
  cpmMax: number | null;
  hasSearchBid: boolean;
  hasRecomBid: boolean;
  breakdown: { total: number; works: number; wastes: number; wasted: number };
  spark: number[];
  trend: { drr: number | null; cpo: number | null };
  features: {
    limits: { on: boolean; day: number | null };
    schedule: { on: boolean };
    topup: { on: boolean };
  };
  state: State;
}

/**
 * По чему сортируется список.
 *
 * КЛЮЧИ — ЭТО КЛЮЧИ КОЛОНОК, буквально: `ColKey` плюс два порядка,
 * у которых своего столбца нет, — по названию и по состоянию.
 *
 * ЗДЕСЬ БЫЛ СВОЙ СПИСОК ИЗ ЧЕТЫРНАДЦАТИ ИМЁН, И ОН ОТСТАЛ. Колонки
 * «Клик, ₽» и «Лимит» появились в таблице, в этот список их не внесли,
 * а заголовок приводил ключ к `SortKey` через `as` — то есть молча.
 * Нажатие на такой заголовок ставило сортировку, которой `valueOf`
 * не знает: сравнение получало `undefined`, `x - Number(y)` давало
 * NaN, и таблица просто не сортировалась. Без единой ошибки в консоли.
 *
 * Теперь список выводится из колонок, `valueOf` разбирает его целиком,
 * и новая колонка без сортировки не скомпилируется.
 */
export type SortKey = ColKey | 'name' | 'state' | 'главное';

export const SORTS: Array<{ key: SortKey; title: string }> = [
  { key: 'главное', title: 'сначала под управлением' },
  { key: 'state', title: 'по состоянию' },
  { key: 'spend', title: 'по расходу' },
  { key: 'drr', title: 'по ДРР' },
  { key: 'cpo', title: 'по цене заказа' },
  { key: 'orders', title: 'по заказам' },
  { key: 'name', title: 'по названию' },
];

/**
 * ═════════════════════════════════════════════════════════════════════
 * «ЗАВЕРШЕНЫ» ЗДЕСЬ НЕТ, И ЭТО НЕ ЗАБЫТО — ЭТО РЕШЕНО.
 *
 * Кнопка была в эталоне и стояла здесь с 26.08.2026. 28.08 владелец
 * решил убрать завершённые из списка совсем: в живом кабинете их 428
 * из 464, и живые тридцать шесть в них тонут. Сервер их больше
 * не отдаёт (`campaigns()` в `src/rk/read.repo.prisma.ts`), а значит
 * кнопка показывала бы вечный ноль.
 *
 * ЭТО ОСОЗНАННОЕ ОТСТУПЛЕНИЕ ОТ ЭТАЛОНА — единственное на этом экране,
 * и владелец его подтвердил отдельным ответом.
 *
 * ВЕЧНЫЙ НОЛЬ ЗДЕСЬ УЖЕ ХОРОНИЛИ: ровно поэтому с плиток убрана
 * «ДРР по кабинету» (см. `src/rk/overview.spec.ts`). Место занято,
 * ответа нет — а человек всё равно нажмёт и решит, что сломалось.
 *
 * ВЕРНУТЬ КНОПКУ БЕЗ СЕРВЕРА НЕЛЬЗЯ: сперва `campaigns()` перестанет
 * отбрасывать статусы 7 и 8, иначе она снова покажет ноль. Обе стороны
 * связаны сторожем `src/ops/spisok-bez-arhiva.spec.ts`.
 * ═════════════════════════════════════════════════════════════════════
 */
export type FilterKey = 'all' | 'managed' | 'unmanaged' | 'wasting';

/**
 * ═════════════════════════════════════════════════════════════════════
 * ЧЕТЫРЕ КНОПКИ ВМЕСТО ШЕСТИ — РЕШЕНИЕ ВЛАДЕЛЬЦА 10.09.2026.
 *
 * Дословно: «Здесь нужно прописать меньше кнопок: все, под управлением,
 * без управления, сливают бюджет. „Работают и стоят по правилам“ — это
 * всё в 1 под управлением. Остальные без управления, сливают бюджет,
 * те которые не проходят по цели, т.е. с красным цветом и всё что хуже».
 *
 * ЧТО УШЛО И ПОЧЕМУ ЭТО НЕ ПОТЕРЯ. «Работают», «не тратят», «стоят
 * по правилам», «на паузе» и «без лимита» отвечали на вопрос
 * «в каком состоянии кампания», и все пять состояний по-прежнему видны
 * — в самой строке, плашкой состояния и значками возможностей.
 * Кнопки отвечали дважды и на пять ладов, а вопросов у человека,
 * открывшего биддер, ровно два: «что сервис ведёт сам» и «что сейчас
 * жжёт деньги».
 *
 * ЭТО ОСОЗНАННОЕ ОТСТУПЛЕНИЕ ОТ ЭТАЛОНА — второе на этом экране
 * (первое, «завершены», — выше). Оба сделаны по прямой просьбе
 * владельца, оба записаны здесь, чтобы следующая сверка с эталоном
 * не приняла их за расхождение и не «починила» обратно.
 *
 * ПОДПИСИ С ЗАГЛАВНОЙ — 14.09.2026, тоже по просьбе владельца: «тут
 * всё написано с маленькой буквы, это неправильно, должно быть
 * с большой». Строчные достались от его же записки 10.09, где он
 * перечислял кнопки в строку, — перечисление приняли за написание.
 * Слова те же, буква первая другая.
 * ═════════════════════════════════════════════════════════════════════
 */
export const FILTERS: Array<{ key: FilterKey; title: string; hint: string }> = [
  /* «ВСЕ» И «ЖИВЫЕ» ТЕПЕРЬ ОДНО И ТО ЖЕ — подсказка не врёт: архив
     сюда не приезжает вовсе, и отбирать «все» больше не из чего. */
  { key: 'all', title: 'Все', hint: 'Все живые кампании кабинета' },
  {
    key: 'managed',
    title: 'Под управлением',
    hint: 'Сервис ведёт их сам: держит ставки, следит за лимитом и правилами. Сюда входят и те, что сейчас работают, и те, что стоят по нашим правилам',
  },
  {
    key: 'unmanaged',
    title: 'Без управления',
    hint: 'Сервис их не трогает: ни ставок, ни правил, ни лимитов. Они живут так, как их оставили в Wildberries',
  },
  {
    key: 'wasting',
    title: 'Сливают бюджет',
    hint: 'ДРР выше предела цели — и те, у кого расход есть, а заказов нет вовсе: там ДРР не существует, и это хуже любого красного',
  },
];

/**
 * @param предел предел ДРР кабинета из целей. `null` — цель не задана,
 *   и «сливают бюджет» тогда судит только по «расход есть, заказов нет»:
 *   красить нечем, а деньги без продаж остаются деньгами без продаж.
 */
export function matches(item: Item, filter: FilterKey, предел: number | null = null): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'managed':
      return item.managed;
    case 'unmanaged':
      return !item.managed;
    case 'wasting':
      return сливает(item, предел);
  }
}

/**
 * ═════════════════════════════════════════════════════════════════════
 * «СЛИВАЕТ» — ЭТО КРАСНЫЙ ПО ЦЕЛИ И ТО, ЧТО ХУЖЕ КРАСНОГО.
 *
 * Красный — `goalClass` возвращает `'bad'`: ДРР выше предела больше чем
 * в `ГРАНЬ` раз. Та же лестница, что красит графу ДРР, что печатает
 * вердикт в разборе и что подписывает окно целей; четвёртого счёта
 * здесь нет нарочно — он разошёлся бы с цветом в графе, и кнопка
 * отбирала бы не то, что видно глазом.
 *
 * ХУЖЕ КРАСНОГО — РАСХОД БЕЗ ЗАКАЗОВ. ДРР там не существует вовсе
 * (делить не на что), в графе стоит прочерк, и `goalClass` честно
 * отдаёт `null`. Судить по цвету — значит не заметить ровно ту
 * кампанию, ради которой кнопку и просили: деньги ушли, продаж нет.
 * Владелец так и сказал: «с красным цветом И ВСЁ ЧТО ХУЖЕ».
 * ═════════════════════════════════════════════════════════════════════
 */
export function сливает(item: Item, предел: number | null): boolean {
  if (goalClass(item.drr, предел) === 'bad') return true;
  return item.spend > 0 && item.orders === 0;
}

/**
 * Значение, по которому сравниваем. `null` — сравнивать не из чего.
 *
 * `null` НЕ ЗАМЕНЯЕТСЯ НУЛЁМ. Кампания без выручки при сортировке
 * по ДРР оказалась бы лучшей в кабинете; кампания без заказов при
 * сортировке по цене заказа — самой дешёвой.
 */
export function valueOf(item: Item, key: SortKey): number | string | null {
  switch (key) {
    case 'spend':
      return item.spend;
    case 'orders':
      return item.orders;
    // ДРР И ЦЕНА ЗАКАЗА БЕРУТСЯ С СЕРВЕРА, а не считаются здесь заново.
    // Прежде их давал `economicsOf`, и формулы жили в двух местах: одна
    // для сортировки, другая для столбца. Разойдись они — и таблица
    // отсортировалась бы не по тому, что в ней написано.
    case 'drr':
      return item.drr;
    case 'cpo':
      return item.cpo;
    case 'budget':
      return item.balance;
    case 'views':
      return item.views;
    case 'clicks':
      return item.clicks;
    case 'ctr':
      return item.ctr;
    case 'atbs':
      return item.atbs;
    case 'crCart':
      return item.crCart;
    case 'crOrder':
      return item.crOrder;
    case 'revenue':
      return item.revenue;
    case 'cpc':
      return item.cpc;
    case 'canceled':
      return item.canceled;
    case 'spendYest':
      return item.spendYest;
    /**
     * ВИЛКА СОРТИРУЕТСЯ ПО ВЕРХНЕМУ КРАЮ. Вопрос при сортировке
     * по ставке всегда один — «где ставим дороже всего».
     */
    case 'cpmBid':
      return item.cpmMax;
    /**
     * ЛИМИТ УЧАСТВУЕТ, ТОЛЬКО ПОКА ОН ДЕЙСТВУЕТ, — так же, как он
     * показывается в столбце. Отсортировать по числу, которого
     * в ячейке нет, значит расставить строки по невидимому.
     */
    case 'limitDay':
      return item.features.limits.on ? item.features.limits.day : null;
    // СВОИМИ ЯЧЕЙКАМИ НЕ СОРТИРУЮТ. Полоска фотографий, столбики
    // динамики и значки правил числом не выражаются; заголовок
    // над ними кнопки сортировки и не рисует.
    case 'products':
    case 'spark':
    case 'rules':
      return null;
    case 'name':
      return (item.name ?? String(item.advertId)).toLowerCase();
    case 'state':
      return rank(item.state);
    /* Сюда не доходит: `arrange` разбирает этот ключ до сравнения.
       Ветка нужна, чтобы разбор оставался полным, — без неё новый
       ключ прошёл бы мимо типов молча. */
    case 'главное':
      return ступень(item);
  }
}

/** По убыванию для денег, по возрастанию для имени, состояния и порядка. */
export function descendingByDefault(key: SortKey): boolean {
  return key !== 'name' && key !== 'state' && key !== 'главное';
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОРЯДОК ПО УМОЛЧАНИЮ: СНАЧАЛА ТО, ЧЕМ МЫ УПРАВЛЯЕМ.
 *
 * ЧТО СКАЗАЛ ВЛАДЕЛЕЦ. «Сейчас чтобы найти подключенную кампанию,
 * которой управляет бидра, нужно скролить». Список шёл по расходу,
 * и управляемая кампания с расходом в триста рублей оказывалась
 * тридцать восьмой — ниже трёх десятков чужих, к которым сервис
 * не имеет отношения.
 *
 * ПОЧЕМУ РАСХОД БЫЛ НЕВЕРНЫМ УМОЛЧАНИЕМ. Он отвечает на вопрос «где
 * больше денег». Человек, открывающий кабинет, задаёт другой: «что
 * с тем, что я сюда завёл». Кампания под управлением — единственная,
 * на которую он через нас влияет; остальные он смотрит, а не ведёт.
 *
 * ТРИ СТУПЕНИ, А НЕ ДВЕ. «Работающие» отделены от «неактивных» отдельно:
 * между кампанией, которая крутится и тратит, и кампанией на паузе
 * разница больше, чем между двумя паузами. Внутри ступени — по расходу,
 * как было: он остаётся верным ответом на «что здесь крупное».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ступень(item: Item): number {
  if (item.managed) return 0;
  if (item.state.health === 'working') return 1;
  return 2;
}

export function arrange(
  items: Item[],
  filter: FilterKey,
  sort: SortKey,
  desc: boolean,
  предел: number | null = null,
): Item[] {
  const rows = items.filter((i) => matches(i, filter, предел));

  /* ПОРЯДОК ПО УМОЛЧАНИЮ СЧИТАЕТСЯ ОТДЕЛЬНО: он не «значение столбца»,
     а три ступени с расходом внутри каждой. Втиснуть его в `valueOf`
     значило бы кодировать ступень и расход одним числом — приём,
     который живёт ровно до первой кампании с расходом больше множителя. */
  if (sort === 'главное') {
    return [...rows].sort(
      (a, b) => ступень(a) - ступень(b) || (b.spend ?? 0) - (a.spend ?? 0),
    );
  }

  return [...rows].sort((a, b) => {
    const x = valueOf(a, sort);
    const y = valueOf(b, sort);

    // НЕИЗВЕСТНОЕ — В КОНЕЦ ПРИ ЛЮБОМ НАПРАВЛЕНИИ. Оно не «маленькое»
    // и не «большое», оно вне сравнения.
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;

    const cmp = typeof x === 'string' ? x.localeCompare(String(y), 'ru') : x - Number(y);
    return desc ? -cmp : cmp;
  });
}

/** Сколько строк под каждым отбором — для подписи на кнопках. */
export function counts(items: Item[], предел: number | null = null): Record<FilterKey, number> {
  const out = {} as Record<FilterKey, number>;
  for (const f of FILTERS) out[f.key] = items.filter((i) => matches(i, f.key, предел)).length;
  return out;
}
